import { BiButton, BiDrawer, BiLink, BiLogo } from 'bi-web-components-react';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { SignIn } from '../api';
import type { MenuCategory } from '../types';
import { BiIconCross, BiIconMenu, BiIconWorld, BiIconMagnefyingGlass, BiIconAdd } from '@bi-digital/icons-react';
import styles from './menu.module.scss';

export type MenuProps = {
    iconColor: 'white' | 'primary';
    salesForceMyApplicationsUrl: string;
    isAuthorized: boolean;
    langText: string;
    languageUrl: string;
    productSearchUrl: string;
    studentPortalUrl: string;
    menu: MenuCategory[];
    disableCart: boolean;
};

export const Menu = ({
    iconColor,
    isAuthorized,
    langText,
    languageUrl,
    menu,
    productSearchUrl,
    studentPortalUrl,
    salesForceMyApplicationsUrl,
    disableCart,
}: MenuProps) => {
    const { t, i18n } = useTranslation(['common']);
    const [open, setOpen] = useState(false);
    const [openSubmenus, setOpenSubmenus] = useState<string[]>([]);

    const toggleSubmenu = (contentGuid: string) =>
        setOpenSubmenus((prev) =>
            prev.includes(contentGuid) ? prev.filter((item) => item !== contentGuid) : [...prev, contentGuid],
        );

    useEffect(() => {
        const handleSignIn = async () => {
            const params = new URLSearchParams(window.location.search);
            const signInB2c = params.get('signInB2c');

            if (signInB2c === 'true') {
                const currentUrl = new URL(window.location.href);
                currentUrl.searchParams.delete('signInB2c');
                const newUrl = currentUrl.toString();
                window.history.replaceState({ path: newUrl }, '', newUrl);
                if (!isAuthorized) {
                    await SignIn();
                }
            }
        };

        handleSignIn();

        const addSignIn = () => handleSignIn();
        window.addEventListener('signIn', addSignIn); // Listen to "signIn"-events

        return () => {
            window.removeEventListener('signIn', addSignIn);
        };
    }, [isAuthorized]);

    return (
        <>
            <BiButton
                icon
                variant='text'
                dark={iconColor !== 'primary'}
                onClick={() => setOpen(true)}
                aria-label={t('common:menuAriaLabel')}
            >
                <BiIconMenu aria-hidden='true' />
            </BiButton>
            <BiDrawer position='top' isOpen={open} backgroundColor='primary' onClose={() => setOpen(false)}>
                <div className={styles.menu}>
                    <div className={styles.topContainer}>
                        <BiLogo dark international={i18n.language !== 'no'} />
                        <BiButton icon variant='text' dark aria-label={t('close')} onClick={() => setOpen(false)}>
                            <BiIconCross aria-hidden='true' />
                        </BiButton>
                    </div>
                    <div className={styles.menuContainer}>
                        <ul className={styles.menuItems}>
                            {isAuthorized && (
                                <>
                                    <li className={classNames(styles.menuItemLink, styles.onlyOnMobile)}>
                                        <a className={styles.menuLink} href={studentPortalUrl}>
                                            {t('common:portal')}
                                        </a>
                                    </li>
                                    <li
                                        className={classNames(
                                            styles.menuItemLink,
                                            styles.marginBottom,
                                            styles.onlyOnMobile,
                                        )}
                                    >
                                        {!disableCart && (
                                            <a className={styles.menuLink} href={salesForceMyApplicationsUrl}>
                                                {t('common:myApplications')}
                                            </a>
                                        )}
                                    </li>
                                </>
                            )}
                            <li className={styles.onlyOnMobile}>
                                <div className={styles.menuButtons}>
                                    <BiButton
                                        variant='flat'
                                        size='small'
                                        dark
                                        href={isAuthorized ? `${languageUrl}?signInB2c=true` : languageUrl}
                                        stretch
                                    >
                                        {langText}
                                        <BiIconWorld aria-hidden='true' slot='icon-right' />
                                    </BiButton>
                                    <BiButton variant='flat' dark size='small' stretch href={productSearchUrl}>
                                        {t('common:programmesAndCourses')}
                                        <BiIconMagnefyingGlass aria-hidden='true' slot='icon-right' />
                                    </BiButton>
                                </div>
                            </li>
                            {isAuthorized && (
                                <li
                                    role='separator'
                                    className={classNames(styles.separatorWrapper, styles.onlyOnMobile)}
                                >
                                    <hr className={styles.separator}></hr>
                                </li>
                            )}
                            {menu.map((categoryItem) => (
                                <li
                                    className={classNames(styles.menuItem, styles.category)}
                                    key={categoryItem.contentGuid}
                                    id={categoryItem.contentGuid}
                                >
                                    <strong className={classNames(styles.categoryName, styles.notOnMobile)}>
                                        {categoryItem.categoryName}
                                    </strong>
                                    <button
                                        className={classNames(
                                            styles.categoryButton,
                                            styles.categoryName,
                                            styles.onlyOnMobile,
                                        )}
                                        onClick={() => toggleSubmenu(categoryItem.contentGuid)}
                                    >
                                        <BiIconAdd className={styles.icon} />
                                        {categoryItem.categoryName}
                                    </button>
                                    <ul
                                        className={classNames(
                                            styles.categoryItems,
                                            openSubmenus.includes(categoryItem.contentGuid) && styles.isActive,
                                        )}
                                    >
                                        <li>
                                            <BiLink
                                                href={categoryItem.categoryLink}
                                                state='noline'
                                                size='medium'
                                                color='light'
                                                aria-label={`${t('frontPage')} ${categoryItem.categoryName}`}
                                            >
                                                {t('frontPage')}
                                            </BiLink>
                                        </li>
                                        {categoryItem.menuItems.map((item) => (
                                            <li key={item.menuItemGuid}>
                                                <BiLink
                                                    href={item.menuItemLink}
                                                    state='noline'
                                                    size='medium'
                                                    color='light'
                                                >
                                                    {item.menuItemTitle}
                                                </BiLink>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </BiDrawer>
        </>
    );
};
